<template>
  <div class="event-form--content">
    <p>
      Dates in the past cannot be edited but you can still add more dates and change the times for
      any upcoming days.
    </p>

    <div class="form-row form-row--event-datetime">
      <div class="form-cell">
        <FloatLabel variant="on" class="has-icon">
          <IconField iconPosition="left">
            <InputIcon>
              <IconCalendar />
            </InputIcon>
            <DatePicker
              v-model="eventDatesExtra"
              :minDate="minDate"
              :maxDate="maxDate"
              selectionMode="range"
              :manualInput="false"
              @date-select="updateExtraDates"
            />
          </IconField>
          <label>Upcoming Dates *</label>
        </FloatLabel>
      </div>

      <div class="form-cell">
        <FloatLabel variant="on">
          <DatePicker
            id="event_from"
            v-model="eventFrom"
            timeOnly
            :manualInput="false"
            hourFormat="12"
            :stepMinute="15"
            @date-select="updateExtraDates"
          />
          <label for="event_from">Start Time *</label>
        </FloatLabel>
      </div>

      <div class="form-cell">
        <FloatLabel variant="on">
          <DatePicker
            id="event_to"
            v-model="eventTo"
            timeOnly
            :manualInput="false"
            hourFormat="12"
            :stepMinute="15"
            @date-select="updateExtraDates"
          />
          <label for="event_to">End Time *</label>
        </FloatLabel>
      </div>
    </div>

    <div class="exceptions-section">
      <div class="event-form--section-title">
        <span>Schedule by Date</span>
      </div>

      <p>
        You can override specific days and times below to help you streamline your insights to only
        days and times that are relevant. If you make changes above, these overrides will reset.
      </p>

      <ul class="date-overrides" :class="{ 'date-overrides--overnight': anyOvernight }">
        <li class="date-overrides__header">
          <span></span>
          <span><template v-if="anyOvernight">Start </template>Date</span>
          <span>Start Time</span>
          <span v-if="anyOvernight">End Date</span>
          <span>End Time</span>
        </li>
        <li v-for="(date, index) in newEvent?.dates" :key="index">
          <span>
            <Checkbox v-model="dateIncludes" :value="date.day" :disabled="isLocked(date)" />
          </span>

          <span>{{ dayjs(date.day).format('ddd, MMM DD, YYYY') }}</span>

          <DatePicker
            v-model="date.from"
            hourFormat="12"
            timeOnly
            :stepMinute="15"
            :disabled="isLocked(date)"
            @value-change="checkOvernight(date)"
          />

          <span v-if="anyOvernight">
            <template v-if="date.isDateOvernight">
              {{ dayjs(date.day).add(1, 'day').format('ddd, MMM DD, YYYY') }}
            </template>
          </span>

          <DatePicker
            v-model="date.to"
            hourFormat="12"
            timeOnly
            :stepMinute="15"
            :disabled="isLocked(date)"
            @value-change="checkOvernight(date)"
          />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import dayjs from 'dayjs';

import { Checkbox, DatePicker, FloatLabel, IconField, InputIcon } from 'primevue';

import type { EventDate, EventTypeNew } from '@/types/Event';

import IconCalendar from '@/icons/events/IconCalendar.svg?component';

const eventDatesExtra = ref<Date[]>([]);

const newEvent = defineModel<EventTypeNew>('newEvent');
const dateIncludes = defineModel<string[]>('dateIncludes');

const eventFrom = ref<Date>(dayjs().hour(9).minute(0).toDate());
const eventTo = ref<Date>(dayjs().hour(17).minute(0).toDate());

const minDate = dayjs().toDate();
const maxDate = dayjs().add(3, 'months').toDate();

const anyOvernight = computed(() => {
  let hasOvernight = false;

  newEvent.value?.dates.forEach((date) => {
    if (dayjs(date.from).get('hour') >= dayjs(date.to).get('hour')) {
      hasOvernight = true;
    }
  });

  return hasOvernight;
});

const checkOvernight = (date: EventDate) => {
  const hour = dayjs(date.to).get('hour');
  const min = dayjs(date.to).get('minute');

  if (dayjs(date.from).hour() >= dayjs(date.to).hour()) {
    date.isDateOvernight = true;
    date.to = dayjs(date.from).add(1, 'day').set('hour', hour).set('minute', min).toDate();
  } else {
    date.isDateOvernight = false;
    date.to = dayjs(date.from).set('hour', hour).set('minute', min).toDate();
  }
};

const updateExtraDates = () => {
  const firstLocked = eventDatesLocked.value[0];

  if (firstLocked && newEvent.value) {
    newEvent.value.dates = [...eventDatesLocked.value];

    const newEventList: EventDate[] = eventDatesLocked.value;

    const startDate = dayjs(eventDatesExtra.value[0]);
    let endDate = eventDatesExtra.value[1] ? dayjs(eventDatesExtra.value[1]) : startDate;

    let currentDay = startDate;

    const endsOvernight = endDate.get('hour') < startDate.get('hour');
    if (endsOvernight) {
      endDate = endDate.add(1, 'day');
    }

    const endTime = dayjs(eventTo.value);

    while (currentDay.isBefore(endDate)) {
      let isDateOvernight = false;
      let currentEnd = currentDay.set('hour', endTime.hour()).set('minute', endTime.minute());

      if (currentDay.isAfter(currentEnd)) {
        isDateOvernight = true;
        currentEnd = currentDay
          .add(1, 'day')
          .set('hour', endTime.hour())
          .set('minute', endTime.minute());
      }

      const eventDate = {
        day: currentDay.format('YYYY-MM-DD'),
        date: currentDay.format('YYYY-MM-DD'),
        start: currentDay.format('HH:mm'),
        end: currentEnd.format('HH:mm'),
        from: currentDay.toDate(),
        to: currentEnd.toDate(),
        isDateOvernight: isDateOvernight,
      };

      newEventList.push(eventDate);
      dateIncludes.value?.push(currentDay.format('YYYY-MM-DD'));
      currentDay = currentDay.add(1, 'day');
    }

    newEventList.sort((a: EventDate, b: EventDate) => {
      return dayjs(b.date).isAfter(a.date) ? -1 : 0;
    });

    newEvent.value.dates = newEventList;
    newEvent.value.event_end = dayjs(newEventList[newEventList.length - 1].to).format();

    // const fakeResp = {
    //   data: {
    //     success: true,
    //     message: 'Event dates changed successfully',
    //   },
    // };
    // toast.success(fakeResp);
  }
};

const isLocked = (date: EventDate) => {
  let hasLock = false;

  eventDatesLocked.value.forEach((lockedDate) => {
    if (lockedDate.day === date.day) {
      hasLock = true;
    }
  });

  return hasLock;
};

const allExtraDates = computed(() => {
  if (newEvent.value) {
    return [...newEvent.value.dates].filter((date) => {
      return dayjs(date.from).isAfter(dayjs());
    });
  }

  return [];
});

const eventDatesLocked = computed(() => {
  const lockedDates: EventDate[] = [];

  newEvent.value?.dates.map((date) => {
    if (date.start && date.end) {
      const start = dayjs(date.from).get('hour');
      const end = dayjs(date.to).get('hour');
      date.isDateOvernight = start >= end;
    } else {
      date.isDateOvernight = false;
    }

    if (dayjs(date.from).isBefore(dayjs())) {
      lockedDates.push(date);
    }
    return date;
  });

  return lockedDates;
});

onMounted(() => {
  if (allExtraDates.value && allExtraDates.value.length) {
    eventDatesExtra.value = [
      dayjs(allExtraDates.value[0].from).toDate(),
      dayjs(allExtraDates.value[allExtraDates.value.length - 1].from).toDate(),
    ];
  }
});
</script>
